import { ThemeColors } from "@/config/colors";
import { Box, Typography } from "@mui/material";
import Image from "next/image";
import type { CSSProperties } from "react";

type Crop = {
	top: number;
	left: number;
	width: number;
	height: number;
};

type MediaLogo = {
	src: string;
	alt: string;
	sourceWidth: number;
	sourceHeight: number;
	frameWidth: number;
	frameHeight: number;
	crop?: Crop;
};

type VideoGroup = {
	station: MediaLogo;
	programs: readonly MediaLogo[];
};

const logo = (
	src: string,
	alt: string,
	sourceWidth: number,
	sourceHeight: number,
	frameWidth: number,
	frameHeight: number,
	crop?: Crop,
): MediaLogo => ({
	src,
	alt,
	sourceWidth,
	sourceHeight,
	frameWidth,
	frameHeight,
	crop,
});

const videoGroups: readonly VideoGroup[] = [
	{
		station: logo("/top/media/video/ntv.png", "日本テレビ", 188, 86, 75, 34),
		programs: [
			logo("/top/media/video/zip.svg", "ZIP!", 70, 30, 55, 23),
			logo("/top/media/video/the-social.png", "the SOCIAL", 430, 153, 64, 22),
		],
	},
	{
		station: logo("/top/media/video/tbs.svg", "TBS", 88, 40, 70, 32),
		programs: [
			logo("/top/media/video/n-sta.jpg", "Nスタ", 960, 540, 53, 22, {
				top: -25.59,
				left: -8.11,
				width: 116.22,
				height: 151.17,
			}),
			logo("/top/media/video/dooo.png", "Dooo", 400, 377, 38, 40, {
				top: -0.3,
				left: -4.53,
				width: 115.25,
				height: 103.42,
			}),
		],
	},
	{
		station: logo(
			"/top/media/video/tv-asahi.svg",
			"テレビ朝日",
			178,
			37,
			142,
			29,
		),
		programs: [
			logo(
				"/top/media/video/kizuki-no-tobira.png",
				"気づきの扉",
				356,
				254,
				58,
				37,
				{
					top: -11.93,
					left: -1.75,
					width: 104.09,
					height: 116.51,
				},
			),
		],
	},
	{
		station: logo(
			"/top/media/video/fuji-tv.svg",
			"フジテレビ",
			101,
			98,
			81,
			79,
		),
		programs: [
			logo(
				"/top/media/video/news-alpha-plus.jpg",
				"News αプラス",
				900,
				900,
				53,
				37,
				{
					top: -38.74,
					left: -8.39,
					width: 115.53,
					height: 167.57,
				},
			),
		],
	},
	{
		station: logo(
			"/top/media/video/tokyo-mx.png",
			"TOKYO MX",
			285,
			158,
			118,
			66,
		),
		programs: [
			logo("/top/media/video/oharina.jpg", "おはリナ！", 399, 501, 65, 45, {
				top: -51.21,
				left: -5.54,
				width: 110.53,
				height: 202.02,
			}),
		],
	},
	{
		station: logo(
			"/top/media/video/newspicks.png",
			"NewsPicks",
			150,
			148,
			70,
			69,
		),
		programs: [
			logo("/top/media/video/the-update.png", "The UPDATE", 540, 284, 62, 43, {
				top: -6.25,
				left: -36.79,
				width: 175.03,
				height: 132.29,
			}),
		],
	},
];

const newspapers: readonly MediaLogo[] = [
	logo("/top/media/newspaper/asahi.png", "朝日新聞", 160, 33, 80, 17),
	logo("/top/media/newspaper/mainichi.png", "毎日新聞", 204, 38, 102, 19),
	logo("/top/media/newspaper/yomiuri.png", "読売新聞", 160, 36, 80, 18),
];

const magazines: readonly MediaLogo[] = [
	logo("/top/media/magazine/crea.svg", "CREA", 140, 31, 105, 24),
	logo("/top/media/magazine/kelly.png", "KELLY", 231, 94, 92, 38),
	logo("/top/media/magazine/spring.svg", "SPRiNG", 139, 28, 104, 21),
	logo("/top/media/magazine/and-rosy.png", "&ROSY", 616, 97, 100, 23, {
		top: 0,
		left: -24.34,
		width: 148.43,
		height: 100,
	}),
	logo("/top/media/magazine/sweet.svg", "sweet", 136, 34, 102, 26),
	logo("/top/media/magazine/biteki.svg", "美的", 88, 45, 66, 34),
	logo("/top/media/magazine/mini.svg", "mini", 118, 35, 89, 26),
	logo("/top/media/magazine/and-girl.png", "andGIRL", 242, 50, 113, 23),
	logo(
		"/top/media/magazine/hiyoko-club.jpg",
		"ひよこクラブ",
		619,
		384,
		104,
		30,
		{
			top: -74.42,
			left: -130.98,
			width: 244.66,
			height: 526.03,
		},
	),
	logo("/top/media/magazine/non-no.jpg", "non-no", 447, 447, 128, 28, {
		top: -187.8,
		left: -8.92,
		width: 117.32,
		height: 545.12,
	}),
	logo("/top/media/magazine/baila.png", "BAILA", 412, 92, 112, 25),
];

const webMedia: readonly MediaLogo[] = [
	logo("/top/media/web/aera-dot.png", "AERA dot.", 172, 46, 86, 23),
	logo("/top/media/web/asahi-digital.png", "朝日新聞 DIGITAL", 172, 52, 86, 26),
	logo("/top/media/web/gendai-business.png", "現代ビジネス", 200, 38, 100, 19),
	logo("/top/media/web/telling.png", "telling.", 200, 44, 100, 22),
	logo(
		"/top/media/web/business-insider.png",
		"BUSINESS INSIDER",
		180,
		60,
		90,
		30,
	),
	logo(
		"/top/media/web/president-online.png",
		"PRESIDENT Online",
		252,
		24,
		126,
		12,
	),
	logo(
		"/top/media/web/president-woman.png",
		"PRESIDENT WOMAN",
		200,
		40,
		100,
		20,
	),
	logo("/top/media/web/blogos.png", "BLOGOS", 200, 34, 100, 17),
	logo("/top/media/web/yomidoctor.png", "yomiDr.", 200, 48, 100, 24),
	logo("/top/media/web/elle-girl.svg", "ELLE girl", 118, 54, 89, 41),
	logo("/top/media/web/huffpost.png", "HuffPost", 674, 296, 140, 38, {
		top: -50.85,
		left: -11.58,
		width: 123.17,
		height: 201.69,
	}),
	logo("/top/media/web/nikkei-doors.jpg", "日経doors", 554, 151, 128, 35),
];

const MediaLogoFrame = ({ logo }: { logo: MediaLogo }) => {
	const imageStyle: CSSProperties = logo.crop
		? {
				position: "absolute",
				top: `${logo.crop.top}%`,
				left: `${logo.crop.left}%`,
				width: `${logo.crop.width}%`,
				height: `${logo.crop.height}%`,
				maxWidth: "none",
				objectFit: "fill",
			}
		: {
				display: "block",
				width: "100%",
				height: "100%",
				objectFit: "contain",
			};

	return (
		<Box
			component="span"
			position="relative"
			display="block"
			flexShrink={0}
			overflow="hidden"
			width={logo.frameWidth}
			height={logo.frameHeight}
		>
			<Image
				src={logo.src}
				alt={logo.alt}
				width={logo.sourceWidth}
				height={logo.sourceHeight}
				sizes={`${logo.frameWidth}px`}
				style={imageStyle}
			/>
		</Box>
	);
};

const CategoryHeading = ({
	children,
	mt = 0,
}: { children: string; mt?: number }) => (
	<Typography
		component="h3"
		fontSize={14}
		fontWeight={700}
		letterSpacing="0.8px"
		textAlign="center"
		mt={mt}
	>
		{children}
	</Typography>
);

const LogoGrid = ({
	logos,
	rowGap,
}: {
	logos: readonly MediaLogo[];
	rowGap: number;
}) => (
	<Box
		component="ul"
		sx={{
			mt: 3.5,
			p: 0,
			mInline: 0,
			mb: 0,
			listStyle: "none",
			display: "flex",
			flexWrap: "wrap",
			alignItems: "center",
			justifyContent: "center",
			columnGap: "16px",
			rowGap: `${rowGap}px`,
			"& > li": {
				width: "calc(50% - 8px)",
				minHeight: 42,
				display: "flex",
				alignItems: "center",
				justifyContent: "center",
			},
		}}
	>
		{logos.map((mediaLogo) => (
			<Box component="li" key={mediaLogo.alt}>
				<MediaLogoFrame logo={mediaLogo} />
			</Box>
		))}
	</Box>
);

export const MediaCoverage = () => (
	<Box
		component="section"
		px={3}
		mt={10}
		textAlign="center"
		aria-labelledby="media-coverage-title"
	>
		<Typography
			id="media-coverage-title"
			variant="h6"
			component="h2"
			color={ThemeColors[0].colors.primary[600]}
		>
			メディア掲載実績
		</Typography>

		<Box mt={5}>
			<CategoryHeading>映像</CategoryHeading>
			<Box
				component="ul"
				sx={{
					mt: 3,
					p: 0,
					mInline: 0,
					mb: 0,
					listStyle: "none",
					display: "grid",
					gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
					gap: "40px 16px",
				}}
			>
				{videoGroups.map((group) => (
					<Box
						component="li"
						key={group.station.alt}
						display="flex"
						flexDirection="column"
						alignItems="center"
						justifyContent="center"
						gap={2}
					>
						<MediaLogoFrame logo={group.station} />
						<Box
							display="flex"
							alignItems="center"
							justifyContent="center"
							gap="12px"
						>
							{group.programs.map((program) => (
								<MediaLogoFrame key={program.alt} logo={program} />
							))}
						</Box>
					</Box>
				))}
			</Box>

			<CategoryHeading mt={7}>新聞</CategoryHeading>
			<Box
				component="ul"
				sx={{
					mt: 3,
					p: 0,
					mInline: 0,
					mb: 0,
					listStyle: "none",
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
					gap: "16px",
				}}
			>
				{newspapers.map((mediaLogo) => (
					<Box component="li" key={mediaLogo.alt} display="flex">
						<MediaLogoFrame logo={mediaLogo} />
					</Box>
				))}
			</Box>

			<CategoryHeading mt={7}>雑誌</CategoryHeading>
			<LogoGrid logos={magazines} rowGap={36} />

			<CategoryHeading mt={7}>Webメディア</CategoryHeading>
			<LogoGrid logos={webMedia} rowGap={40} />
		</Box>
	</Box>
);
